import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useSearchParams } from "react-router";
import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Input,
  Menu,
  Modal,
  PageBody,
  Pill,
  SegmentedControl,
  Select,
  useToast,
  type MenuItem,
  type SortState,
} from "@astrum/ui";
import { bytesToMb, uploadLimitFor } from "@astrum/shared";
import { api, ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { formatBytes, iconForType } from "../components/AttachmentList.js";
import { downloadArchive, downloadFile, fileUrl, isDesktop, openFile, uploadFileWithProgress } from "../lib/transport.js";
import { uploadFailure, uploadThrownMessage } from "../lib/upload-error.js";
import { isOpenable, isPendingScan, useScanPolling } from "../lib/scan-status.js";
import { ScanBadge } from "../components/ScanBadge.js";
import { UploadProgress } from "../components/UploadProgress.js";
import { downscaleImage } from "../lib/downscale-image.js";
import { FilePreview } from "../components/FilePreview.js";
import { ImageLightbox } from "../components/ImageLightbox.js";
import { DocumentViewer, VIEWABLE_DOCUMENT_TYPES } from "../components/DocumentViewer.js";
import { FileViewer, type ViewedFile } from "../components/FileViewer.js";
import { SharedFolders } from "../components/SharedFolders.js";
import { opensInFileViewer } from "../lib/file-preview.js";
import { useFileDrop } from "../lib/useFileDrop.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./files.css";

/** Matches the breakpoint every other route switches its mobile layout at
 * (work.css, schedule.css, admin.css). Below it, DataTable's horizontal
 * scroll is not a fair substitute for five real-width columns — on a phone
 * the name column ran to the edge of the screen with no visible hint that
 * "Shared in", "Shared by" and "Added" existed off to the right at all. */
const MOBILE_QUERY = "(max-width: 640px)";

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

/**
 * Everything shared, findable without remembering where it was shared.
 *
 * Files were reachable only through the task or message they were attached to,
 * which means finding one required already knowing where it is — the opposite
 * of what a library is for.
 *
 * Two views for two questions. The list answers "which spreadsheet did she
 * send", where a filename and a date are the whole answer. The grid answers
 * "which of these is the right photograph", where a filename answers nothing.
 *
 * Folders are a personal shelf laid over that library, not a second place
 * files live — filing something away never changes who else can already see
 * it, only where it sits when you're the one looking.
 */

type FileRow = {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploaderId: string;
  uploaderName: string;
  channelId: string | null;
  projectId: string | null;
  taskId: string | null;
  folderId: string | null;
  /** pending | clean | infected | failed — pending files are listed but not openable. */
  scanStatus?: string;
  createdAt: string;
};

type FolderRow = { id: string; name: string; parentFolderId: string | null };

const KINDS = [
  { value: "", label: "Any type" },
  { value: "image", label: "Images" },
  { value: "video", label: "Videos" },
  { value: "pdf", label: "PDFs" },
  { value: "document", label: "Documents" },
  { value: "spreadsheet", label: "Spreadsheets" },
  { value: "archive", label: "Archives" },
  { value: "other", label: "Other" },
];

/** Types the preview endpoint serves inline — the rest get an icon. */
const PREVIEWABLE = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

function whereFrom(file: FileRow): { label: string; to: string } | null {
  if (file.taskId) return { label: "Task", to: `/work/task/${file.taskId}` };
  if (file.channelId) return { label: "Conversation", to: `/chat/${file.channelId}` };
  if (file.projectId) return { label: "Project", to: `/work/project/${file.projectId}` };
  return null;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: sameYear ? undefined : "numeric",
  });
}

function exportFilesCsv(rows: FileRow[]): void {
  const csv = toCsv(
    ["File name", "Size", "Uploader", "Shared in", "Added"],
    rows,
    (file) => [
      file.fileName,
      formatBytes(file.sizeBytes),
      file.uploaderName,
      whereFrom(file)?.label ?? "Personal",
      formatWhen(file.createdAt),
    ],
  );
  downloadCsv(`files-${new Date().toLocaleDateString("en-CA")}.csv`, csv);
}

/**
 * Every action for one file, behind a single kebab rather than a row of icon
 * buttons — five actions is one too many to lay out flat across a mobile
 * card, a grid tile, and a desktop table row without it looking different in
 * each.
 */
function FileMenu({
  file,
  canEdit,
  canDelete,
  onRename,
  onMoveToFolder,
  onAttachToTask,
  onViewVersions,
  onPreview,
  onDelete,
}: {
  file: FileRow;
  canEdit: boolean;
  canDelete: boolean;
  onRename: () => void;
  onMoveToFolder: () => void;
  onAttachToTask: () => void;
  onViewVersions: () => void;
  /** Opens the in-app viewer; only offered for types it can show. */
  onPreview?: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const copyLink = useCallback(() => {
    const link = `${window.location.origin}${fileUrl(`/api/v1/files/${file.id}/download`)}`;
    navigator.clipboard
      .writeText(link)
      .then(() => toast("Link copied."))
      .catch(() => toast("Could not copy — select and copy the link manually.", { tone: "danger" }));
  }, [file.id, toast]);

  // Nothing that opens the bytes is offered while the scan is unfinished.
  const openable = isOpenable(file);
  const items: MenuItem[] = [
    ...(openable && onPreview && VIEWABLE_DOCUMENT_TYPES.has(file.contentType)
      ? [{ id: "preview", label: "Preview", icon: "eye" as const, onSelect: onPreview }]
      : []),
    ...(openable
      ? [
          {
            id: "download",
            label: "Download",
            icon: "download" as const,
            onSelect: () => void downloadFile(`/api/v1/files/${file.id}/download`, file.fileName),
          },
          { id: "copy-link", label: "Copy link", icon: "link" as const, onSelect: copyLink },
        ]
      : []),
    { id: "versions", label: "Version history", icon: "clock", onSelect: onViewVersions },
  ];
  if (canEdit) {
    items.push(
      { id: "rename", label: "Rename", icon: "edit", onSelect: onRename },
      { id: "move", label: "Move to folder", icon: "document", onSelect: onMoveToFolder },
    );
    // Attaching to a task only makes sense for a file with nowhere else it
    // already belongs — the same rule the server enforces, surfaced here so
    // the option is not offered only to be refused.
    if (!file.channelId && !file.projectId) {
      items.push({ id: "attach-task", label: file.taskId ? "Change linked task" : "Attach to a task", icon: "check", onSelect: onAttachToTask });
    }
  }
  if (canDelete) {
    items.push({ id: "delete", label: "Delete", icon: "trash", destructive: true, onSelect: onDelete });
  }

  return (
    <Menu open={open} onClose={() => setOpen(false)} label={`Actions for ${file.fileName}`} items={items}>
      <IconButton
        icon={<Icon name="more" size={14} />}
        label={`Actions for ${file.fileName}`}
        size="sm"
        variant="ghost"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

type FileVersion = {
  id: string;
  version: number;
  fileName: string;
  sizeBytes: number;
  uploaderId: string;
  createdAt: string;
  isCurrent: boolean;
};

/** Lists a file's version history. Only the current version is downloadable;
 *  older versions' bytes may already have been reclaimed past retention. */
function VersionsModal({ file, onClose }: { file: FileRow | null; onClose: () => void }) {
  const [versions, setVersions] = useState<FileVersion[] | null>(null);

  useEffect(() => {
    if (!file) {
      setVersions(null);
      return;
    }
    let cancelled = false;
    void api
      .get<{ versions: FileVersion[] }>(`/files/${file.id}/versions`)
      .then((res) => {
        if (!cancelled) setVersions(res.versions);
      })
      .catch(() => {
        if (!cancelled) setVersions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  return (
    <Modal open={!!file} onClose={onClose} title="Version history">
      <div className="files__form">
        {versions === null ? (
          <p className="files__muted">Loading versions…</p>
        ) : versions.length <= 1 ? (
          <p className="files__muted">This file has no earlier versions.</p>
        ) : (
          <div className="files__folder-list">
            {versions.map((v) => (
              <div key={v.id} className={`files__folder-option ${v.isCurrent ? "is-active" : ""}`}>
                <Icon name="clock" size={13} />
                <span>
                  v{v.version}
                  {v.isCurrent ? " (current)" : ""} — {formatBytes(v.sizeBytes)} ·{" "}
                  {new Date(v.createdAt).toLocaleDateString()}
                </span>
                {v.isCurrent && (
                  <IconButton
                    icon={<Icon name="download" size={13} />}
                    label={`Download version ${v.version}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => void downloadFile(`/api/v1/files/${v.id}/download`, v.fileName)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function FolderChip({
  folder,
  isActive,
  onOpen,
  onDelete,
  onDropFiles,
}: {
  folder: FolderRow;
  isActive: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onDropFiles: (files: File[]) => void;
}) {
  // Its own drop zone, separate from the page-wide one — a file dropped
  // directly on a folder chip files into that folder specifically, even
  // while a different folder (or none) is open.
  const { isOver, dropZoneProps } = useFileDrop(onDropFiles);
  return (
    <span
      className={`files__folder-chip ${isActive ? "is-active" : ""} ${isOver ? "is-drop-target" : ""}`}
      {...dropZoneProps}
    >
      <button type="button" className="files__folder-chip-label" onClick={onOpen}>
        {folder.name}
      </button>
      <IconButton
        icon={<Icon name="close" size={11} />}
        label={`Delete folder "${folder.name}"`}
        size="sm"
        variant="ghost"
        onClick={onDelete}
      />
    </span>
  );
}

export function Files() {
  usePageTitle("Files");
  const { toast } = useToast();
  const { viewer } = useAuth();
  const [params, setParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [lightboxImage, setLightboxImage] = useState<{ id: string; fileName: string } | null>(null);
  const [viewedDocument, setViewedDocument] = useState<{ id: string; fileName: string } | null>(null);
  // Video, audio, text/CSV, xlsx and Office documents.
  const [viewedFile, setViewedFile] = useState<ViewedFile | null>(null);

  /*
   * The href stays on every file-name/tile link below — for web and for
   * right-click / open-in-new-tab — but the click itself is intercepted to
   * open a lightbox (images), the document viewer (PDFs), or, on desktop,
   * whose webview has no tabs, the file in its default application
   * (everything else). See openFile in transport.ts.
   */
  const openOnClick = useCallback(
    (file: FileRow) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      // Still being scanned: the download route would refuse it.
      if (!isOpenable(file)) {
        event.preventDefault();
        return;
      }
      if (PREVIEWABLE.has(file.contentType)) {
        event.preventDefault();
        setLightboxImage({ id: file.id, fileName: file.fileName });
        return;
      }
      if (VIEWABLE_DOCUMENT_TYPES.has(file.contentType)) {
        event.preventDefault();
        setViewedDocument({ id: file.id, fileName: file.fileName });
        return;
      }
      if (opensInFileViewer(file.contentType)) {
        event.preventDefault();
        setViewedFile({ id: file.id, fileName: file.fileName, contentType: file.contentType });
        return;
      }
      if (!isDesktop) return;
      event.preventDefault();
      void openFile(`/api/v1/files/${file.id}/download`, file.fileName).catch(() =>
        toast(`Could not open ${file.fileName}.`, { tone: "danger" }),
      );
    },
    [toast],
  );

  const [files, setFiles] = useState<FileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<FileRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState<FolderRow | null>(null);
  const [deleteFolderBusy, setDeleteFolderBusy] = useState(false);
  const [renaming, setRenaming] = useState<FileRow | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [moving, setMoving] = useState<FileRow | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [attaching, setAttaching] = useState<FileRow | null>(null);
  const [viewingVersions, setViewingVersions] = useState<FileRow | null>(null);
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachableTasks, setAttachableTasks] = useState<{ id: string; title: string }[] | null>(null);

  /* Bulk selection — list view only; the grid and mobile card layouts have no
     row to check. Selection is cleared whenever the underlying rows change,
     the same rule Admin's table follows, since a selected id may no longer
     be part of what's on screen after a filter or reload. */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMoving, setBulkMoving] = useState(false);
  const [bulkMoveBusy, setBulkMoveBusy] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [bulkDownloadBusy, setBulkDownloadBusy] = useState(false);

  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderBusy, setNewFolderBusy] = useState(false);

  const kind = params.get("kind") ?? "";
  const search = params.get("q") ?? "";
  const view = params.get("view") === "grid" ? "grid" : "list";
  /* Everything readable by default: "my uploads" is not a library. */
  const scope = params.get("scope") === "mine" ? "mine" : "all";
  const folderId = params.get("folder") ?? "";
  const inFolders = params.get("space") === "folders";

  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (!value) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  /* Folders are always "mine" — the server enforces this too, but fetching
     them only once per visit rather than per filter change keeps the folder
     rail from flickering while the file list itself refetches. */
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ folders: FolderRow[] }>("/file-folders")
      .then((res) => !cancelled && setFolders(res.folders))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      const query = new URLSearchParams({ scope, limit: "200" });
      if (kind) query.set("kind", kind);
      if (search) query.set("search", search);
      if (folderId) query.set("folderId", folderId);

      try {
        const res = await api.get<{ files: FileRow[] }>(`/files?${query.toString()}`);
        if (cancelled) return;
        setFiles(res.files);
        setSelectedIds(new Set());
      } catch (error) {
        if (cancelled) return;
        toast(error instanceof ApiError ? error.message : "Could not load files.", { tone: "danger" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [kind, search, scope, folderId, toast]);

  // Scan completion has no realtime event, so while any listed file is still
  // being scanned, re-read the same listing and update statuses in place
  // (without the loading flash a full reload would cause).
  const refreshScanStatuses = useCallback(() => {
    const query = new URLSearchParams({ scope, limit: "200" });
    if (kind) query.set("kind", kind);
    if (search) query.set("search", search);
    if (folderId) query.set("folderId", folderId);
    api
      .get<{ files: FileRow[] }>(`/files?${query.toString()}`)
      .then((res) => {
        const status = new Map(res.files.map((f) => [f.id, f.scanStatus]));
        setFiles((prev) => prev.map((f) => (status.has(f.id) ? { ...f, scanStatus: status.get(f.id) } : f)));
      })
      .catch(() => undefined);
  }, [kind, search, scope, folderId]);
  useScanPolling(files.some(isPendingScan), refreshScanStatuses);

  const isFiltered = Boolean(kind || search || folderId);

  const gridItems = files;

  const [sort, setSort] = useState<SortState>({ key: "when", dir: "desc" });

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const withKey = (file: FileRow): string | number => {
      switch (sort.key) {
        case "name":
          return file.fileName.toLowerCase();
        case "size":
          return file.sizeBytes;
        case "where":
          return whereFrom(file)?.label ?? "";
        case "uploader":
          return file.uploaderName.toLowerCase();
        case "when":
        default:
          return file.createdAt;
      }
    };
    return [...files].sort((a, b) => {
      const av = withKey(a);
      const bv = withKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [files, sort]);

  const shown = view === "grid" ? gridItems : sorted;

  // Mirrors the API's own rule: only the uploader or an admin may edit or delete.
  const canEdit = useCallback(
    (file: FileRow) => Boolean(viewer) && (file.uploaderId === viewer!.id || viewer!.role === "admin"),
    [viewer],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/files/${deleting.id}`);
      setFiles((prev) => prev.filter((file) => file.id !== deleting.id));
      toast("File deleted.", { tone: "ok" });
      setDeleting(null);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not delete the file.", { tone: "danger" });
    } finally {
      setDeleteBusy(false);
    }
  }, [deleting, toast]);

  const confirmRename = useCallback(async () => {
    if (!renaming) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    setRenameBusy(true);
    try {
      await api.patch<{ file: { fileName: string } }>(`/files/${renaming.id}`, { fileName: trimmed });
      setFiles((prev) => prev.map((f) => (f.id === renaming.id ? { ...f, fileName: trimmed } : f)));
      setRenaming(null);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not rename the file.", { tone: "danger" });
    } finally {
      setRenameBusy(false);
    }
  }, [renaming, renameValue, toast]);

  const confirmMove = useCallback(
    async (targetFolderId: string | null) => {
      if (!moving) return;
      setMoveBusy(true);
      try {
        await api.patch(`/files/${moving.id}`, { folderId: targetFolderId });
        setFiles((prev) => prev.map((f) => (f.id === moving.id ? { ...f, folderId: targetFolderId } : f)));
        setMoving(null);
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not move the file.", { tone: "danger" });
      } finally {
        setMoveBusy(false);
      }
    },
    [moving, toast],
  );

  const confirmBulkMove = useCallback(
    async (targetFolderId: string | null) => {
      const ids = [...selectedIds];
      if (ids.length === 0) return;
      setBulkMoveBusy(true);
      try {
        const res = await api.post<{ moved: number }>("/files/bulk-move", { fileIds: ids, folderId: targetFolderId });
        setFiles((prev) => prev.map((f) => (selectedIds.has(f.id) ? { ...f, folderId: targetFolderId } : f)));
        setSelectedIds(new Set());
        setBulkMoving(false);
        toast(
          res.moved < ids.length
            ? `${res.moved} of ${ids.length} files moved. The rest are not yours to move.`
            : `${res.moved} ${res.moved === 1 ? "file" : "files"} moved.`,
          { tone: "ok" },
        );
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not move those files.", { tone: "danger" });
      } finally {
        setBulkMoveBusy(false);
      }
    },
    [selectedIds, toast],
  );

  const bulkDownload = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    // A single selection is a plain file, not an archive — save it directly so
    // the person gets "photo.png", not a one-item zip they have to unpack.
    if (ids.length === 1) {
      const only = files.find((f) => f.id === ids[0]);
      if (!only) return;
      setBulkDownloadBusy(true);
      try {
        await downloadFile(`/api/v1/files/${only.id}/download`, only.fileName);
      } catch {
        toast("Could not download that file.", { tone: "danger" });
      } finally {
        setBulkDownloadBusy(false);
      }
      return;
    }
    setBulkDownloadBusy(true);
    try {
      const stamp = new Date().toLocaleDateString("en-CA");
      await downloadArchive("/files/bulk/download", ids, `astrum-files-${stamp}.zip`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not download those files.", { tone: "danger" });
    } finally {
      setBulkDownloadBusy(false);
    }
  }, [selectedIds, files, toast]);

  const confirmBulkDelete = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkDeleteBusy(true);
    try {
      const res = await api.delete<{ deleted: number }>("/files/bulk", { fileIds: ids });
      setFiles((prev) => prev.filter((f) => !selectedIds.has(f.id)));
      setSelectedIds(new Set());
      setBulkDeleting(false);
      toast(
        res.deleted < ids.length
          ? `${res.deleted} of ${ids.length} files deleted. The rest are not yours to delete.`
          : `${res.deleted} ${res.deleted === 1 ? "file" : "files"} deleted.`,
        { tone: "ok" },
      );
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not delete those files.", { tone: "danger" });
    } finally {
      setBulkDeleteBusy(false);
    }
  }, [selectedIds, toast]);

  const openAttach = useCallback((file: FileRow) => {
    setAttaching(file);
    setAttachableTasks(null);
    api
      .get<{ tasks: { id: string; title: string }[] }>("/tasks?assignee=me&limit=100")
      .then((res) => setAttachableTasks(res.tasks))
      .catch(() => setAttachableTasks([]));
  }, []);

  const confirmAttach = useCallback(
    async (taskId: string | null) => {
      if (!attaching) return;
      setAttachBusy(true);
      try {
        await api.patch(`/files/${attaching.id}`, { taskId });
        setFiles((prev) => prev.map((f) => (f.id === attaching.id ? { ...f, taskId } : f)));
        setAttaching(null);
        toast(taskId ? "Attached to the task." : "Detached from the task.", { tone: "ok" });
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not attach that file.", { tone: "danger" });
      } finally {
        setAttachBusy(false);
      }
    },
    [attaching, toast],
  );

  /*
   * The library's own upload path — every other one attaches a file to a
   * channel, task, or project as a side effect of something else. A file
   * with nothing to say what it belongs to is fully supported server-side
   * (POST /files accepts zero scope fields; canReadFile already treats an
   * unattached file as its uploader's alone) so there's no reason someone
   * who just wants to park a file here needs to go create a task first.
   */
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ fileName: string; fraction: number } | null>(null);

  const upload = useCallback(
    // `targetFolderId` defaults to whatever folder is open — the same
    // "upload here" behavior as before — but can be overridden, which is
    // what dropping a file directly onto a folder chip needs: that upload
    // belongs in the chip's folder regardless of which one is currently open.
    (file: File, targetFolderId: string | undefined = folderId) => {
      // Reject an over-limit file up front, with the real reason, rather than
      // streaming it all only for the server to refuse it.
      const limit = uploadLimitFor(file.type);
      if (file.size > limit) {
        toast(`${file.name}: files must be under ${bytesToMb(limit)}MB.`, { tone: "danger" });
        return;
      }
      setUploading(true);
      void downscaleImage(file).then((prepared) => {
        const form = new FormData();
        form.append("file", prepared);
        setUploadProgress({ fileName: file.name, fraction: 0 });
        uploadFileWithProgress("/files", form, (fraction) => setUploadProgress({ fileName: file.name, fraction }))
          .promise.then(async (result) => {
            if (!result.ok) throw uploadFailure(result);
            const payload = result.body as { file: FileRow };
            let uploaded: FileRow = { ...payload.file, channelId: null, projectId: null, taskId: null, folderId: null };
            if (targetFolderId) {
              await api.patch(`/files/${uploaded.id}`, { folderId: targetFolderId });
              uploaded = { ...uploaded, folderId: targetFolderId };
            }
            setFiles((prev) => [uploaded, ...prev]);
            toast(`${file.name} uploaded.`);
          })
          .catch((error: unknown) => {
            toast(`${file.name}: ${uploadThrownMessage(error)}`, { tone: "danger" });
          })
          .finally(() => {
            setUploading(false);
            setUploadProgress(null);
            if (uploadInputRef.current) uploadInputRef.current.value = "";
          });
      });
    },
    [folderId, toast],
  );

  // The page-wide drop zone: files dropped anywhere over the list land in
  // whichever folder is currently open, same as clicking Upload does.
  const { isOver: isDraggingOverPage, dropZoneProps: pageDropZoneProps } = useFileDrop(
    useCallback((droppedFiles) => droppedFiles.forEach((file) => upload(file)), [upload]),
  );

  const createFolder = useCallback(() => {
    const name = newFolderName.trim();
    if (!name) return;
    setNewFolderBusy(true);
    api
      .post<{ folder: FolderRow }>("/file-folders", { name })
      .then((res) => {
        setFolders((prev) => [...prev, res.folder]);
        setNewFolderName("");
        setNewFolderOpen(false);
      })
      .catch((error: unknown) => toast(error instanceof ApiError ? error.message : "Could not create the folder.", { tone: "danger" }))
      .finally(() => setNewFolderBusy(false));
  }, [newFolderName, toast]);

  const confirmDeleteFolder = useCallback(async () => {
    if (!deletingFolder) return;
    const folder = deletingFolder;
    setDeleteFolderBusy(true);
    try {
      await api.delete(`/file-folders/${folder.id}`);
      setFolders((prev) => prev.filter((f) => f.id !== folder.id));
      if (folderId === folder.id) updateParams({ folder: undefined });
      // Files filed under it are not deleted, only un-filed — the same
      // guarantee the server makes.
      setFiles((prev) => prev.map((f) => (f.folderId === folder.id ? { ...f, folderId: null } : f)));
      setDeletingFolder(null);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not delete the folder.", { tone: "danger" });
    } finally {
      setDeleteFolderBusy(false);
    }
  }, [deletingFolder, folderId, toast, updateParams]);

  const activeFolder = folders.find((f) => f.id === folderId);

  // A file opened from the shared/project folder browser.
  const openFromFolder = useCallback((file: ViewedFile) => {
    if (PREVIEWABLE.has(file.contentType)) setLightboxImage({ id: file.id, fileName: file.fileName });
    else if (VIEWABLE_DOCUMENT_TYPES.has(file.contentType)) setViewedDocument({ id: file.id, fileName: file.fileName });
    else if (opensInFileViewer(file.contentType)) setViewedFile(file);
    else void downloadFile(`/api/v1/files/${file.id}/download`, file.fileName).catch(() => toast("Could not download the file.", { tone: "danger" }));
  }, [toast]);

  const actionsFor = (file: FileRow) => (
    <FileMenu
      file={file}
      canEdit={canEdit(file)}
      canDelete={canEdit(file)}
      onRename={() => {
        setRenaming(file);
        setRenameValue(file.fileName);
      }}
      onMoveToFolder={() => setMoving(file)}
      onAttachToTask={() => openAttach(file)}
      onViewVersions={() => setViewingVersions(file)}
      onPreview={() => setViewedDocument({ id: file.id, fileName: file.fileName })}
      onDelete={() => setDeleting(file)}
    />
  );

  return (
    <>
      <PageBody
        title="Files"
        subtitle={`${files.length} shared with you`}
        width="wide"
        actions={
          <>
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              aria-label="Upload files"
              className="files__upload-input"
              onChange={(e) => {
                Array.from(e.target.files ?? []).forEach((file) => upload(file));
              }}
            />
            <Button
              variant="primary"
              size="sm"
              loading={uploading}
              icon={<Icon name="attach" size={14} />}
              onClick={() => uploadInputRef.current?.click()}
            >
              Upload
            </Button>
            {uploadProgress && <UploadProgress fileName={uploadProgress.fileName} fraction={uploadProgress.fraction} />}
            {shown.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="download" size={14} />}
                onClick={() => exportFilesCsv(shown)}
              >
                Export CSV
              </Button>
            )}
            <SegmentedControl
              aria-label="View"
              value={view}
              onChange={(value) => updateParams({ view: value === "grid" ? "grid" : undefined })}
              options={[
                { value: "list", label: "List" },
                { value: "grid", label: "Grid" },
              ]}
            />
          </>
        }
      >
        <SegmentedControl
          aria-label="Files area"
          value={inFolders ? "folders" : "files"}
          onChange={(value) => updateParams({ space: value === "folders" ? "folders" : undefined })}
          options={[
            { value: "files", label: "All files" },
            { value: "folders", label: "Shared & project folders" },
          ]}
        />
        {inFolders ? (
          <SharedFolders onOpenFile={openFromFolder} />
        ) : (
          <>
        <div className="files__folders">
          <button
            type="button"
            className={`files__folder-chip ${!folderId ? "is-active" : ""}`}
            onClick={() => updateParams({ folder: undefined })}
          >
            <Icon name="document" size={13} />
            All files
          </button>
          {folders.map((folder) => (
            <FolderChip
              key={folder.id}
              folder={folder}
              isActive={folderId === folder.id}
              onOpen={() => updateParams({ folder: folder.id })}
              onDelete={() => setDeletingFolder(folder)}
              onDropFiles={(droppedFiles) => droppedFiles.forEach((file) => upload(file, folder.id))}
            />
          ))}
          <button type="button" className="files__folder-add" onClick={() => setNewFolderOpen(true)}>
            <Icon name="plus" size={13} />
            New folder
          </button>
        </div>

        <div className="files__filters">
          <label className="files__search">
            <Icon name="search" size={14} />
            <input
              type="search"
              placeholder="Search by file name…"
              value={searchDraft}
              aria-label="Search files"
              onChange={(event) => {
                const value = event.target.value;
                setSearchDraft(value);
                clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => updateParams({ q: value || undefined }), 250);
              }}
            />
          </label>

          <Select
            aria-label="Filter by type"
            value={kind}
            onChange={(event) => updateParams({ kind: event.target.value || undefined })}
          >
            {KINDS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Whose files"
            value={scope}
            onChange={(event) => updateParams({ scope: event.target.value === "mine" ? "mine" : undefined })}
          >
            <option value="all">Everything I can see</option>
            <option value="mine">Only mine</option>
          </Select>
        </div>

        {view === "list" && !isMobile && selectedIds.size > 0 && (
          <div className="files__bulk-toolbar">
            <span className="files__bulk-count">{selectedIds.size} selected</span>
            <Button size="sm" variant="secondary" loading={bulkDownloadBusy} onClick={() => void bulkDownload()}>
              Download
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setBulkMoving(true)}>
              Move to folder
            </Button>
            <Button size="sm" variant="danger" onClick={() => setBulkDeleting(true)}>
              Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </div>
        )}

        <div className={`files__drop-zone ${isDraggingOverPage ? "is-drop-target" : ""}`} {...pageDropZoneProps}>
        {isDraggingOverPage && (
          <div className="files__drop-overlay">
            <div className="files__drop-card">
              <Icon name="attach" size={24} />
              <span>Drop to upload{activeFolder ? ` to "${activeFolder.name}"` : ""}</span>
            </div>
          </div>
        )}
        {view === "grid" ? (
          loading ? (
            <div className="files__grid">
              {Array.from({ length: 12 }, (_, index) => (
                <div key={index} className="files__tile files__tile--skeleton" />
              ))}
            </div>
          ) : gridItems.length === 0 ? (
            <EmptyState
              icon="eye"
              title="No files to show"
              description={activeFolder ? `Nothing is filed under "${activeFolder.name}" yet.` : "Files shared with you will appear here."}
            />
          ) : (
            <div className="files__grid">
              {gridItems.map((file) => (
                <div key={file.id} className="files__tile" title={file.fileName}>
                  <a
                    href={fileUrl(`/api/v1/files/${file.id}/download`)}
                    onClick={openOnClick(file)}
                    className="files__tile-link"
                  >
                    {PREVIEWABLE.has(file.contentType) && isOpenable(file) ? (
                      <FilePreview fileId={file.id} alt={file.fileName} />
                    ) : (
                      <div className="files__tile-icon">
                        <Icon name={iconForType(file.contentType)} size={32} />
                      </div>
                    )}
                    <span className="files__tile-name">{file.fileName}</span>
                    <ScanBadge file={file} />
                  </a>
                  <div className="files__tile-actions">{actionsFor(file)}</div>
                </div>
              ))}
            </div>
          )
        ) : isMobile ? (
          /*
           * DataTable's horizontal scroll is not a fair substitute for five
           * real-width columns on a phone: the name column ran to the edge of
           * the screen with no visible hint that "Shared in", "Shared by" and
           * "Added" existed off to the right at all. Below the same
           * breakpoint every other route switches its mobile layout at, each
           * row becomes a card instead — everything stacked, nothing to
           * scroll sideways to find.
           */
          loading ? (
            <div className="files__cards">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="files__card files__card--skeleton" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <EmptyState
              icon="document"
              title={isFiltered ? "Nothing matches" : "No files yet"}
              description={
                isFiltered
                  ? "Try a different search, type, or folder."
                  : "Files shared in conversations, tasks and projects appear here."
              }
            />
          ) : (
            <div className="files__cards">
              {shown.map((file) => {
                const where = whereFrom(file);
                return (
                  <div key={file.id} className="files__card">
                    <a
                      className="files__card-name"
                      href={fileUrl(`/api/v1/files/${file.id}/download`)}
                      onClick={openOnClick(file)}
                    >
                      <Icon name={iconForType(file.contentType)} size={16} />
                      <span>{file.fileName}</span>
                    </a>
                    <ScanBadge file={file} />
                    <div className="files__card-meta">
                      <span className="files__muted">{formatBytes(file.sizeBytes)}</span>
                      <span className="files__muted">·</span>
                      {where ? (
                        <a className="files__where" href={where.to}>
                          {where.label}
                        </a>
                      ) : (
                        <Pill tone="neutral">Personal</Pill>
                      )}
                      <span className="files__muted">·</span>
                      <span className="files__muted">{formatWhen(file.createdAt)}</span>
                    </div>
                    <div className="files__card-foot">
                      <span className="files__person">
                        <Avatar name={file.uploaderName} size={18} />
                        <span className="files__muted">{file.uploaderName}</span>
                      </span>
                      <span className="files__actions">{actionsFor(file)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <DataTable
            caption="Files shared with you"
            density="compact"
            rows={shown}
            rowKey={(file) => file.id}
            loading={loading}
            selectable
            selected={selectedIds}
            onSelectedChange={setSelectedIds}
            sort={sort}
            onSortChange={setSort}
            empty={
              <EmptyState
                icon="document"
                title={isFiltered ? "Nothing matches" : "No files yet"}
                description={
                  isFiltered
                    ? "Try a different search, type, or folder."
                    : "Files shared in conversations, tasks and projects appear here."
                }
              />
            }
            columns={[
              {
                key: "name",
                header: "Name",
                sticky: true,
                sortable: true,
                render: (file) => (
                  <a
                    className="files__name"
                    href={fileUrl(`/api/v1/files/${file.id}/download`)}
                    onClick={openOnClick(file)}
                  >
                    <Icon name={iconForType(file.contentType)} size={15} />
                    <span>{file.fileName}</span>
                    <ScanBadge file={file} />
                  </a>
                ),
              },
              {
                key: "size",
                header: "Size",
                width: "100px",
                align: "end",
                sortable: true,
                render: (file) => <span className="files__muted">{formatBytes(file.sizeBytes)}</span>,
              },
              {
                key: "where",
                header: "Shared in",
                width: "160px",
                sortable: true,
                render: (file) => {
                  const where = whereFrom(file);
                  return where ? (
                    <a className="files__where" href={where.to}>
                      {where.label}
                    </a>
                  ) : (
                    <Pill tone="neutral">Personal</Pill>
                  );
                },
              },
              {
                key: "uploader",
                header: "Shared by",
                width: "190px",
                sortable: true,
                render: (file) => (
                  <span className="files__person">
                    <Avatar name={file.uploaderName} size={20} />
                    <span className="files__muted">{file.uploaderName}</span>
                  </span>
                ),
              },
              {
                key: "when",
                header: "Added",
                width: "120px",
                sortable: true,
                render: (file) => <span className="files__muted">{formatWhen(file.createdAt)}</span>,
              },
              {
                key: "actions",
                header: "",
                width: "56px",
                align: "end",
                render: (file) => actionsFor(file),
              },
            ]}
          />
        )}
        </div>
          </>
        )}
      </PageBody>

      <Modal open={!!deleting} onClose={() => setDeleting(null)} title="Delete this file?">
        <p className="files__delete-copy">
          {deleting ? `"${deleting.fileName}" will be removed for everyone who could see it.` : ""} This cannot be
          undone.
        </p>
        <div className="files__delete-actions">
          <Button variant="ghost" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleteBusy} onClick={() => void confirmDelete()}>
            Delete file
          </Button>
        </div>
      </Modal>

      <Modal open={!!deletingFolder} onClose={() => setDeletingFolder(null)} title="Delete this folder?">
        <p className="files__delete-copy">
          {deletingFolder ? `"${deletingFolder.name}" will be deleted.` : ""} Files inside are only un-filed,
          not deleted — they will still be available from the main file list. This cannot be undone.
        </p>
        <div className="files__delete-actions">
          <Button variant="ghost" onClick={() => setDeletingFolder(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleteFolderBusy} onClick={() => void confirmDeleteFolder()}>
            Delete folder
          </Button>
        </div>
      </Modal>

      <Modal open={!!renaming} onClose={() => setRenaming(null)} title="Rename file">
        <form
          className="files__form"
          onSubmit={(e) => {
            e.preventDefault();
            void confirmRename();
          }}
        >
          <Input label="File name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus required />
          <div className="files__delete-actions">
            <Button type="button" variant="ghost" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button type="submit" loading={renameBusy} disabled={!renameValue.trim()}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!moving} onClose={() => setMoving(null)} title="Move to folder">
        <div className="files__form">
          <div className="files__folder-list">
            <button
              type="button"
              className={`files__folder-option ${moving && !moving.folderId ? "is-active" : ""}`}
              disabled={moveBusy}
              onClick={() => void confirmMove(null)}
            >
              <Icon name="close" size={13} />
              No folder
            </button>
            {folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className={`files__folder-option ${moving?.folderId === folder.id ? "is-active" : ""}`}
                disabled={moveBusy}
                onClick={() => void confirmMove(folder.id)}
              >
                <Icon name="document" size={13} />
                {folder.name}
              </button>
            ))}
            {folders.length === 0 && (
              <p className="files__muted">No folders yet — create one from the Files page first.</p>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={bulkMoving} onClose={() => setBulkMoving(false)} title={`Move ${selectedIds.size} files to folder`}>
        <div className="files__form">
          <div className="files__folder-list">
            <button
              type="button"
              className="files__folder-option"
              disabled={bulkMoveBusy}
              onClick={() => void confirmBulkMove(null)}
            >
              <Icon name="close" size={13} />
              No folder
            </button>
            {folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className="files__folder-option"
                disabled={bulkMoveBusy}
                onClick={() => void confirmBulkMove(folder.id)}
              >
                <Icon name="document" size={13} />
                {folder.name}
              </button>
            ))}
            {folders.length === 0 && (
              <p className="files__muted">No folders yet — create one from the Files page first.</p>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={bulkDeleting} onClose={() => setBulkDeleting(false)} title={`Delete ${selectedIds.size} files?`}>
        <p className="files__delete-copy">
          {selectedIds.size} {selectedIds.size === 1 ? "file" : "files"} will be removed for everyone who could see
          them. This cannot be undone.
        </p>
        <div className="files__delete-actions">
          <Button variant="ghost" onClick={() => setBulkDeleting(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={bulkDeleteBusy} onClick={() => void confirmBulkDelete()}>
            Delete files
          </Button>
        </div>
      </Modal>

      <Modal open={!!attaching} onClose={() => setAttaching(null)} title="Attach to a task">
        <div className="files__form">
          {attaching?.taskId && (
            <button type="button" className="files__folder-option" disabled={attachBusy} onClick={() => void confirmAttach(null)}>
              <Icon name="close" size={13} />
              Detach from its current task
            </button>
          )}
          {attachableTasks === null ? (
            <p className="files__muted">Loading your tasks…</p>
          ) : attachableTasks.length === 0 ? (
            <p className="files__muted">No tasks assigned to you yet.</p>
          ) : (
            <div className="files__folder-list">
              {attachableTasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  className={`files__folder-option ${attaching?.taskId === task.id ? "is-active" : ""}`}
                  disabled={attachBusy}
                  onClick={() => void confirmAttach(task.id)}
                >
                  <Icon name="check" size={13} />
                  {task.title}
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <VersionsModal file={viewingVersions} onClose={() => setViewingVersions(null)} />

      <Modal open={newFolderOpen} onClose={() => setNewFolderOpen(false)} title="New folder">
        <form
          className="files__form"
          onSubmit={(e) => {
            e.preventDefault();
            createFolder();
          }}
        >
          <Input
            label="Folder name"
            placeholder="Contracts"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            autoFocus
            required
          />
          <div className="files__delete-actions">
            <Button type="button" variant="ghost" onClick={() => setNewFolderOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={newFolderBusy} disabled={!newFolderName.trim()}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <ImageLightbox
        open={lightboxImage !== null}
        fileId={lightboxImage?.id ?? ""}
        fileName={lightboxImage?.fileName ?? ""}
        onClose={() => setLightboxImage(null)}
      />
      <FileViewer file={viewedFile} onClose={() => setViewedFile(null)} />
      <DocumentViewer
        open={viewedDocument !== null}
        fileId={viewedDocument?.id ?? ""}
        fileName={viewedDocument?.fileName ?? ""}
        onClose={() => setViewedDocument(null)}
      />
    </>
  );
}
